import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "./supabaseClient";

let channelSequence = 0;

/** Nom de channel unique (deux abonnements au même nom se gênent dans supabase-js). */
export function uniqueChannelName(prefix: string): string {
  channelSequence += 1;
  return `${prefix}:${channelSequence}`;
}

/** Délai minimal entre deux rattrapages déclenchés par le retour sur l'onglet. */
const VISIBILITY_RESYNC_MIN_INTERVAL_MS = 5_000;

/**
 * Abonne `channel` et déclenche `onResync` (rechargement complet) pour rattraper
 * les événements manqués :
 * - quand le channel repasse SUBSCRIBED après une coupure (pas au premier abonnement :
 *   le chargement initial est fait par l'appelant), ou dès le premier SUBSCRIBED avec
 *   `resyncOnFirstSubscribe` (ferme le trou entre l'instantané du chargement initial et
 *   le début effectif de l'écoute) ;
 * - quand l'onglet redevient visible.
 * Retourne la fonction de nettoyage (retire le channel et l'écouteur).
 */
export function subscribeWithResync(
  channel: RealtimeChannel,
  onResync: () => void,
  options: { resyncOnFirstSubscribe?: boolean } = {},
): () => void {
  let subscribedOnce = false;
  let lastResyncAt = Date.now();
  let disposed = false;

  const resync = () => {
    if (disposed) return;
    lastResyncAt = Date.now();
    onResync();
  };

  channel.subscribe((status) => {
    if (status === "SUBSCRIBED") {
      if (subscribedOnce || options.resyncOnFirstSubscribe) resync();
      subscribedOnce = true;
    }
  });

  const onVisibilityChange = () => {
    if (document.visibilityState !== "visible") return;
    if (Date.now() - lastResyncAt < VISIBILITY_RESYNC_MIN_INTERVAL_MS) return;
    resync();
  };
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisibilityChange);

  return () => {
    disposed = true;
    if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisibilityChange);
    void supabase.removeChannel(channel);
  };
}
